import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getCurrentUser } from "aws-amplify/auth";
import { FiLoader, FiShare2 } from "react-icons/fi";
import { getApiEndpoint } from "../../config/amplifyOutputs";
import { ChatMessage } from "../../store/aiChatSlice";
import { normalizeAiMessageText } from "../../services/mcpService";
import { parseUpdateBlocks } from "../../utils/chatUpdates";
import AiChatMessages from "../../components/AiChatComponent/panelComponents/AiChatMessages";
import { ReactComponent as ErodeLogo } from "../../assets/images/erode-logo-black.svg";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";

// Public, read-only view of a shared AI-analyst conversation. No Cognito auth required.

const PUBLIC_API_NAME = "sharePublicApi";

const GONE_MESSAGE =
  "The content you are trying to view is no longer available. It is possible the original owner has turned off sharing.";

interface SnapshotKmlAttachment {
  filename: string;
  size: number;
  mimetype: string;
  uri: string;
}

interface SnapshotMessage {
  id: string;
  content: string;
  isUser: boolean;
  createdAt?: string;
  kmlAttachments?: SnapshotKmlAttachment[];
}

interface SharedSnapshot {
  title: string;
  conversationType?: string;
  scope?: string; // "conversation" | "message" | "scheduleRunner" — set by the view Lambda from the share record
  includePrompts?: boolean; // static agent shares: owner chose to show their prompts (view Lambda, from the share record)
  createdAt?: string;
  messages: SnapshotMessage[];
}

type LoadState = "loading" | "success" | "gone";

// Resolve the public REST endpoint base URL from the bundled amplify config.
function getPublicApiBase(): string | null {
  return getApiEndpoint(PUBLIC_API_NAME);
}

const SharedConversationPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const { capture } = usePostHogCapture();

  const [state, setState] = useState<LoadState>("loading");
  const [snapshot, setSnapshot] = useState<SharedSnapshot | null>(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const viewedFiredRef = useRef(false);

  // Keep public share pages out of search indexes. Tokens are unguessable, but a forwarded link
  // must not surface proprietary analysis as a search result (cf. the 2025 indexed-ChatGPT-shares
  // incident). robots.txt disallows /share/ as a backstop; this tags the rendered page itself so
  // crawlers that render JS honor it. Scoped to this route — removed on unmount.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  // Detect auth state without forcing a redirect.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await getCurrentUser();
        if (!cancelled) setIsSignedIn(true);
      } catch {
        if (!cancelled) setIsSignedIn(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch the public snapshot without Cognito.
  useEffect(() => {
    let cancelled = false;
    if (!shareId) {
      setState("gone");
      return;
    }

    (async () => {
      const base = getPublicApiBase();
      const url = base ? `${base}/share/${shareId}` : `/share/${shareId}`;
      try {
        const res = await fetch(url);
        if (!res.ok) {
          // 410 (no longer available) / 404 / anything non-2xx
          if (!cancelled) setState("gone");
          return;
        }
        const data = (await res.json()) as SharedSnapshot;
        if (cancelled) return;
        setSnapshot(data);
        setState("success");
      } catch (err) {
        console.error("[SharedConversationPage] Failed to fetch shared conversation:", err);
        if (!cancelled) setState("gone");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shareId]);

  // Fire the viewed event once after a successful load.
  useEffect(() => {
    if (state === "success" && !viewedFiredRef.current) {
      viewedFiredRef.current = true;
      capture(POSTHOG_EVENTS.SHARED_CONVERSATION_VIEWED, {
        share_id: shareId,
        conversation_type: snapshot?.conversationType,
      });
    }
  }, [state, shareId, snapshot?.conversationType, capture]);

  // Map snapshot messages into the ChatMessage shape AiChatMessages expects.
  // Live (scheduleRunner) shares always show only the AI responses. Static shares of
  // agent conversations hide the prompts too, unless the owner opted in via the share
  // modal's "Include my prompts" checkbox (includePrompts on the share record).
  // Regular chat shares keep the full back-and-forth.
  const messages: ChatMessage[] = useMemo(() => {
    if (!snapshot?.messages) return [];
    const hidePrompts =
      snapshot.scope === "scheduleRunner" ||
      (snapshot.conversationType === "agent" && !snapshot.includePrompts);
    return snapshot.messages
      .filter((m) => !hidePrompts || !m.isUser)
      .map((m) => ({
        id: m.id,
        // Strip streamed <update> progress steps from AI messages — shared
        // conversations show only the final answers.
        text: m.isUser
          ? normalizeAiMessageText(m.content, m.isUser)
          : normalizeAiMessageText(parseUpdateBlocks(m.content).response, m.isUser),
        isUser: m.isUser,
        createdAt: m.createdAt,
        attachments: m.kmlAttachments?.length ? m.kmlAttachments : undefined,
      }));
  }, [snapshot]);

  const [urlCopied, setUrlCopied] = useState(false);
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy page link:", err);
    }
  };

  const handleSignIn = () => {
    capture(POSTHOG_EVENTS.SHARED_SIGNUP_CLICKED, { action: "sign_in", share_id: shareId });
    navigate("/");
  };

  const handleSignUp = () => {
    capture(POSTHOG_EVENTS.SHARED_SIGNUP_CLICKED, { action: "sign_up", share_id: shareId });
    navigate("/?signup=true");
  };

  // Loading state
  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-eai-primary-accent/10 via-white to-white text-gray-500">
        <FiLoader className="mr-2 h-5 w-5 animate-spin" /> Loading shared conversation...
      </div>
    );
  }

  // Gone (revoked / not found) shares the same page shell as a normal conversation —
  // only the card body differs.
  const isGone = state === "gone" || !snapshot;
  const isAgent = !isGone && snapshot?.conversationType === "agent";
  // scheduleRunner shares re-snapshot on each run, so they're "live" (vs. a frozen agent share).
  const isLiveAgentRun = !isGone && snapshot?.scope === "scheduleRunner";
  const sharedDate =
    !isGone && snapshot?.createdAt
      ? new Date(snapshot.createdAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;

  // The response body and sign-in CTA render in both layouts (live fixed-height card vs. the
  // default growing card), so define them once.
  const responseContent =
    messages.length === 0 ? (
      <p className="py-10 text-center text-sm text-gray-400">
        This conversation has no messages to display.
      </p>
    ) : (
      <AiChatMessages
        messages={messages}
        isLoading={false}
        isFetchingConversation={false}
        messagesEndRef={messagesEndRef}
        readOnly
      />
    );

  const signInCta = !isSignedIn ? (
    <div className="rounded-xl bg-eai-primary-accent/5 p-5 text-center ring-1 ring-eai-primary-accent/10">
      <p className="text-base font-semibold text-gray-900">Want to ask more questions like this?</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
        Sign in to your account or sign up for a free trial.
      </p>
      <div className="mx-auto mt-4 flex max-w-xs flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={handleSignUp}
          className="flex-1 rounded-md bg-eai-primary-accent px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-90"
        >
          Sign up
        </button>
        <button
          type="button"
          onClick={handleSignIn}
          className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
        >
          Sign in
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gradient-to-b from-eai-primary-accent/10 via-white to-white">
      {/* Header: logo left-aligned with the card below, title + time right-aligned */}
      <header className="shrink-0 pb-3 pt-6">
        <div className="mx-auto flex max-w-6xl items-end justify-between gap-3 px-6 sm:px-8">
          <a
            href="https://erode.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0"
            aria-label="Erode — erode.ai"
          >
            <ErodeLogo className="h-7 w-auto" aria-label="Erode" />
          </a>
          <div className="flex min-w-0 items-center gap-3">
            <p className="min-w-0 truncate text-xs text-gray-400">
              Shared from Erode AI{sharedDate ? ` · ${sharedDate}` : ""}
            </p>
            {/* Copy-link button with tooltip — hidden for live agent runs, which match the
                lean embed view (no interactive share control). */}
            {!isLiveAgentRun && (
              <div className="group relative shrink-0">
                <button
                  type="button"
                  onClick={handleCopyUrl}
                  aria-label="Copy link to this page"
                  className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white/70 px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900"
                >
                  <FiShare2 className="h-4 w-4" />
                  <span>Share</span>
                </button>
                <span
                  className={`pointer-events-none absolute right-0 top-full z-10 mt-1.5 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-sm transition-opacity duration-150 ${
                    urlCopied ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                  }`}
                >
                  {urlCopied ? "Copied!" : "Copy link"}
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Body: every share type uses a fixed-height rounded card — title/label pinned, only
          the response scrolls (matching the embed view). The CTA stays pinned below the card. */}
      <main className="min-h-0 flex-1 overflow-hidden">
        <div className="mx-auto flex h-full w-full max-w-6xl flex-col px-4 pb-6 pt-3 sm:px-6">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 pb-4 shadow-md ring-1 ring-black/5">
            {isGone ? (
              <div className="flex flex-1 items-center justify-center p-6">
                <p className="px-4 text-center text-base text-gray-700">{GONE_MESSAGE}</p>
              </div>
            ) : (
              <>
                {/* Pinned card header — title with the share-type label stacked beneath it. */}
                <div className="shrink-0 border-b border-gray-200 px-4 pb-3 pt-4 sm:px-6 sm:pt-6">
                  {snapshot?.title && (
                    <h1 className="text-lg font-semibold text-gray-900">
                      {snapshot.title.replace(/^\[Scheduled\]\s*/i, "")}
                    </h1>
                  )}
                  <span className="mt-1 block text-xs font-medium uppercase tracking-wide text-gray-400">
                    {snapshot?.scope === "message"
                      ? "Shared Message"
                      : isLiveAgentRun
                      ? "Live Shared Agent Run"
                      : isAgent
                      ? "Shared Agent Run"
                      : "Shared Conversation"}
                  </span>
                </div>
                {/* Scrollable response — the only part that scrolls. The card's own pb-4
                    keeps the response off the bottom edge at every scroll position. */}
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-4 sm:px-6">{responseContent}</div>
              </>
            )}
          </div>
          {/* CTA stays pinned below the fixed card so it's always visible. */}
          {signInCta && <div className="mt-4 shrink-0">{signInCta}</div>}
        </div>
      </main>

      <footer className="shrink-0 px-4 py-4 sm:px-6">
        <a
          href="https://erode.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="mx-auto flex max-w-6xl items-center justify-center gap-1.5 text-xs text-gray-400 transition-colors hover:text-gray-600"
        >
          <span>Powered by</span>
          <ErodeLogo className="h-3.5 w-auto opacity-60" aria-label="Erode" />
        </a>
      </footer>
    </div>
  );
};

export default SharedConversationPage;
